/**
 * FILE: user-online.service.ts
 * CHỨC NĂNG: Định kỳ gửi trạng thái online của user lên Cloudflare Worker
 *            + Hiển thị trạng thái online trên tab trình duyệt (● / ○).
 * TÁC DỤNG: Giúp server biết user đang online để tính năng chia hội thoại
 *           và báo cáo hoạt động chính xác hơn. Đồng bộ trạng thái giữa
 *           các tab qua BroadcastChannel API.
 *
 * FE13 parity: app.component.ts saveLastActiveTime() + app.service.ts setFavicon()
 */
import {isPlatformBrowser} from '@angular/common';
import {inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {HttpClient} from '@angular/common/http';
import {AuthService} from '@core/authentication/auth.service';
import {SplitConversationService} from '@app/features/conversation/services/split-conversation.service';
import {environment} from '@environments/environment';

// ─── Constants ────────────────────────────────────────────────────────────────

/** FE13 parity: 20s giữa các lần gửi heartbeat */
const HEARTBEAT_INTERVAL = 20_000;

/** FE13 parity: 2 phút không tương tác → chuyển sang away */
const AWAY_TIMEOUT = 120_000;

/** Tên channel BroadcastChannel để đồng bộ trạng thái giữa các tab */
const BROADCAST_CHANNEL = 'count_status_active';

/** Favicon path base từ CDN */
const FAVICON_BASE = environment.cloudStorageStatic.vpageStaticUrl + 'logo/';

/** Favicon paths theo trạng thái */
const FAVICON_PATH = {
  DEFAULT: FAVICON_BASE + 'favicon.svg',
  ACTIVE: FAVICON_BASE + 'favicon-blue-dot.svg',
  AWAY: FAVICON_BASE + 'favicon-yellow-dot.svg',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

type UserOnlineStatus = 'active' | 'away' | 'offline';

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({providedIn: 'root'})
export class UserOnlineService implements OnDestroy {
  // ── DI ────────────────────────────────────────────────────────────────────
  private readonly platformId = inject(PLATFORM_ID);
  private readonly titleService = inject(Title);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly splitService = inject(SplitConversationService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // ── State ─────────────────────────────────────────────────────────────────
  private _status: UserOnlineStatus = 'offline';
  private isRunning = false;

  /** Thời điểm user tương tác lần cuối (Date.now) */
  private lastActivityAt = 0;

  /** Timer gửi heartbeat định kỳ */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  /** Timer chuyển away khi không tương tác */
  private awayTimer: ReturnType<typeof setTimeout> | null = null;

  /** BroadcastChannel để sync giữa các tab */
  private broadcastChannel: BroadcastChannel | null = null;

  /** Base title (không có prefix ●/○) — cập nhật khi phát hiện title thay đổi */
  private baseTitle = '';

  /** MutationObserver theo dõi title thay đổi từ AppService */
  private titleObserver: MutationObserver | null = null;

  // ── Activity events ──────────────────────────────────────────────────────
  private readonly ACTIVITY_EVENTS = [
    'mousemove', 'keydown', 'click', 'scroll', 'touchstart',
  ] as const;

  private readonly boundHandleActivity = this.handleActivity.bind(this);
  private readonly boundHandleVisibility = this.handleVisibilityChange.bind(this);
  private readonly boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);

  // ── Worker URL ────────────────────────────────────────────────────────────
  private get workerUrl(): string {
    const cfg = (environment as any)?.cloudFlareService?.url;
    return cfg?.userActiveUrl ?? '';
  }

  /** Trạng thái hiện tại (active / away / offline) */
  get status(): UserOnlineStatus {
    return this._status;
  }

  /** User có đang active không */
  get isActive(): boolean {
    return this._status === 'active';
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.stop();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Bắt đầu gửi heartbeat + theo dõi activity.
   * Idempotent — gọi nhiều lần không sao.
   * Tự động kiểm tra login + worker URL trước khi chạy.
   */
  start(): void {
    if (!this.isBrowser) return;
    if (this.isRunning) return;
    if (!this.authService.isLoginEd()) return;
    if (!this.workerUrl) return;

    this.isRunning = true;
    this.baseTitle = this.titleService.getTitle();
    this.lastActivityAt = Date.now();

    // Gán status luôn ở active khi start
    this.applyStatus('active');

    // 1. Đăng ký activity listeners
    this.registerActivityListeners();

    // 2. BroadcastChannel — đồng bộ giữa các tab
    this.initBroadcastChannel();

    // 3. MutationObserver — theo dõi title thay đổi từ AppService
    this.watchTitleChanges();

    // 4. visibilitychange — gửi heartbeat ngay khi tab được focus
    document.addEventListener('visibilitychange', this.boundHandleVisibility);

    // 5. beforeunload — báo tab sắp đóng
    window.addEventListener('beforeunload', this.boundHandleBeforeUnload);

    // 6. Start heartbeat
    this.startHeartbeat();

    // 7. Gửi heartbeat ngay lập tức
    this.sendHeartbeat();
  }

  /**
   * Dừng toàn bộ hoạt động, reset tab title + favicon.
   * Idempotent.
   */
  stop(): void {
    if (!this.isBrowser) return;
    if (!this.isRunning) return;

    this.isRunning = false;

    this.stopHeartbeat();
    this.clearAwayTimer();
    this.unregisterActivityListeners();
    this.closeBroadcastChannel();
    this.stopWatchingTitle();

    document.removeEventListener('visibilitychange', this.boundHandleVisibility);
    window.removeEventListener('beforeunload', this.boundHandleBeforeUnload);

    // Reset về trạng thái offline
    this._status = 'offline';
    this.titleService.setTitle(this.baseTitle);
    this.setFavicon(FAVICON_PATH.DEFAULT);
  }

  // ── Activity handling ─────────────────────────────────────────────────────

  private handleActivity(): void {
    if (!this.isRunning) return;

    this.lastActivityAt = Date.now();

    if (this._status !== 'active') {
      this.applyStatus('active');
    }

    // Reset away timer
    this.clearAwayTimer();
    this.awayTimer = setTimeout(() => {
      if (this.isRunning && this._status === 'active') {
        this.applyStatus('away');
      }
    }, AWAY_TIMEOUT);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible' && this.isRunning) {
      this.handleActivity();
      // Gửi heartbeat ngay khi quay lại tab (không chờ interval)
      this.sendHeartbeat();
    }
  }

  private handleBeforeUnload(): void {
    // Báo cho các tab khác biết tab này sắp đóng
    this.broadcast({checkStatusClose: true});
  }

  // ── Status management ─────────────────────────────────────────────────────

  private applyStatus(status: UserOnlineStatus): void {
    this._status = status;
    this.updateTabIndicator();
    // Broadcast trạng thái sang các tab khác
    this.broadcast({status: status === 'active' ? 1 : 2});
  }

  // ── Tab indicator: title + favicon ───────────────────────────────────────

  /** Cập nhật title và favicon theo status hiện tại */
  private updateTabIndicator(): void {
    // Luôn đọc title mới nhất, strip prefix cũ, lưu làm baseTitle
    const raw = this.stripStatusPrefix(this.titleService.getTitle());
    this.baseTitle = raw;

    switch (this._status) {
      case 'active':
        this.titleService.setTitle(raw);
        this.setFavicon(FAVICON_PATH.ACTIVE);
        break;
      case 'away':
        this.titleService.setTitle(raw);
        this.setFavicon(FAVICON_PATH.AWAY);
        break;
      default:
        this.titleService.setTitle(raw);
        this.setFavicon(FAVICON_PATH.DEFAULT);
        break;
    }
  }

  /** Xóa prefix ● hoặc ○ khỏi title */
  private stripStatusPrefix(title: string): string {
    return title.replace(/^[●○]\s*/, '');
  }

  // ── Title change watcher ──────────────────────────────────────────────────

  /**
   * Dùng MutationObserver để phát hiện khi AppService thay đổi title
   * qua Angular Title service, sau đó tự động thêm lại prefix ●/○.
   *
   * ⚠️ Không dùng flag isSettingTitle để tránh loop vì callback
   * MutationObserver là microtask (async), flag sẽ reset trước khi callback chạy.
   * Thay vào đó: kiểm tra title hiện tại đã có prefix đúng chưa → nếu rồi thì skip.
   */
  private watchTitleChanges(): void {
    this.stopWatchingTitle();

    const titleEl = document.querySelector('title');
    if (!titleEl) return;

    this.titleObserver = new MutationObserver(() => {
      if (!this.isRunning) return;

      // Cập nhật baseTitle khi AppService thay đổi title
      this.baseTitle = this.titleService.getTitle();
    });

    this.titleObserver.observe(titleEl, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  private stopWatchingTitle(): void {
    if (this.titleObserver) {
      this.titleObserver.disconnect();
      this.titleObserver = null;
    }
  }

  // ── Favicon ───────────────────────────────────────────────────────────────

  private setFavicon(href: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }

  // ── BroadcastChannel — cross-tab sync ────────────────────────────────────

  private initBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') return;

    try {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL);
      this.broadcastChannel.onmessage = (event: MessageEvent) => {
        const data = event.data || {};

        // Tab khác hỏi thăm → reply status hiện tại
        if (data.checkStatus && this.isRunning) {
          this.broadcast({status: this._status === 'active' ? 1 : 2});
          return;
        }

        // Tab khác đóng → cập nhật activity (để tab này tiếp quản)
        if (data.checkStatusClose && this.isRunning) {
          this.handleActivity();
        }
      };
    } catch {
      // BroadcastChannel không supported → skip
    }
  }

  private broadcast(data: Record<string, unknown>): void {
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(data);
      } catch { /* no-op */ }
    }
  }

  private closeBroadcastChannel(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  // ── Heartbeat ─────────────────────────────────────────────────────────────

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** Gửi POST tới Cloudflare Worker với thông tin online của user */
  private sendHeartbeat(): void {
    if (!this.isRunning) return;

    const userId = this.authService.getUid();
    if (!userId) return;

    const pageIds = this.authService.getPageIds();
    const businessIds = this.authService.getBusinessAllSelectManagement();

    const payload = {
      userId,
      activeAt: Math.floor(Date.now() / 1000), // Unix timestamp (seconds)
      pageIds: pageIds || [],
      businessIds: businessIds || [],
      isOnSplit: this.splitService.pageSplitIds?.length ? 1 : 2,
    };

    // Fire-and-forget — không cần xử lý response
    this.http.post(this.workerUrl, payload).subscribe({
      error: () => { /* silent — không spam console */ },
    });
  }

  // ── Listeners registration ───────────────────────────────────────────────

  private registerActivityListeners(): void {
    for (const event of this.ACTIVITY_EVENTS) {
      window.addEventListener(event, this.boundHandleActivity, {passive: true});
    }
  }

  private unregisterActivityListeners(): void {
    for (const event of this.ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.boundHandleActivity);
    }
  }

  private clearAwayTimer(): void {
    if (this.awayTimer) {
      clearTimeout(this.awayTimer);
      this.awayTimer = null;
    }
  }
}
